import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Clock, 
  BookOpen, 
  Award, 
  Lock, 
  Unlock, 
  Home, 
  ZoomIn, 
  ZoomOut, 
  RotateCcw,
  Play,
  X,
  Users,
  MapPin
} from 'lucide-react';
import { getTimelineEras, getTimelineEvents, getUserTimelineProgress } from '../services/timelineService';
import { TimelineEra, TimelineEvent, UserTimelineProgress } from '../types/timeline';
import authService from '../services/authService';
import LoadingSpinner from '../components/LoadingSpinner';
import './TimelinePage.css';

// Helper function to determine if an event is unlocked
// All events are visible to everyone, but authenticated users can track progress
const isEventUnlocked = (event: TimelineEvent, progress?: UserTimelineProgress) => {
  // Show all events to everyone
  return true;
};

const TimelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
  const [eras, setEras] = useState<TimelineEra[]>([]);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEra, setSelectedEra] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<TimelineEvent | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [unlockProgress, setUnlockProgress] = useState(0);
  const [userProgress, setUserProgress] = useState<UserTimelineProgress | undefined>(undefined);
  
  // Function to organize events by era
  const getEventsByEra = (eraId: string) => {
    return events.filter(event => event.era === eraId)
      .map(event => ({
        ...event,
        isUnlocked: isEventUnlocked(event, userProgress)
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  };
  
  // Calculate the percentage of unlocked events
  const calculateUnlockProgress = () => {
    if (events.length === 0) return 0;
    
    // All events are always available (100% unlocked)
    return 100;
  };

  // Ensure backend login
  useEffect(() => {
    if (isAuthenticated && user) {
      const localUser = authService.getCurrentUser();
      if (!localUser) {
        const syncUser = async () => {
          try {
            const accessToken = await getAccessTokenSilently({
              authorizationParams: {
                audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                scope: 'openid profile email'
              }
            });
            await authService.loginWithAuth0(user, accessToken);
          } catch (err) {
            console.error('Failed to sync user with backend:', err);
          }
        };
        syncUser();
      }
    }
  }, [isAuthenticated, user, getAccessTokenSilently]);

  // Fetch timeline data from API
  useEffect(() => {
    const fetchTimelineData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get timeline eras
        const erasResponse = await getTimelineEras();
        if (erasResponse.success) {
          setEras(erasResponse.data.sort((a, b) => a.sortOrder - b.sortOrder));
        }
        
        // Get timeline events
        const eventsResponse = await getTimelineEvents();
        if (eventsResponse.success) {
          setEvents(eventsResponse.data);
        }
        
        // Try to get user progress if authenticated
        if (isAuthenticated && user) {
          try {
            const localUser = authService.getCurrentUser();
            if (localUser && localUser.auth0Id) {
              const progressResponse = await getUserTimelineProgress(localUser.auth0Id);
              if (progressResponse.success) {
                setUserProgress(progressResponse.data);
              }
            }
          } catch (userProgressError) {
            // If there's an error getting user progress, just continue without it
            console.error('Error fetching user progress:', userProgressError);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching timeline data:', err);
        setError('Failed to load timeline data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchTimelineData();
  }, [isAuthenticated, user]);
  
  // Calculate unlock progress once data is loaded
  useEffect(() => {
    if (events.length > 0) {
      setUnlockProgress(calculateUnlockProgress());
    }
  }, [events, userProgress]);

  const handleEraClick = (eraId: string) => {
    setSelectedEra(selectedEra === eraId ? null : eraId);
    setSelectedEvent(null);
  };

  const handleEventClick = (event: TimelineEvent & { isUnlocked: boolean }) => {
    setSelectedEvent(event);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const resetView = () => {
    setSelectedEra(null);
    setSelectedEvent(null);
    setZoomLevel(1);
  };

  if (loading) {
    return (
      <div className="timeline-container">
        <div className="timeline-loading">
          <Clock size={48} className="timeline-loading-icon" />
          <h2>Loading Biblical Timeline...</h2>
          <p>Traversing through time and Scripture...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="timeline-container">
        <div className="timeline-error">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="timeline-retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="timeline-container">
      {/* Header Section */}
      <div className="timeline-header">
        <button
          onClick={() => navigate('/')}
          className="timeline-home-button"
        >
          <Home size={20} />
          <span>Home</span>
        </button>
        <div className="timeline-header-content">
          <div className="timeline-title-wrapper">
            <BookOpen size={32} className="timeline-title-icon" />
            <h1 className="timeline-title">Biblical Timeline</h1>
          </div>
          <p className="timeline-subtitle">Journey through biblical history</p>
        </div>
        <div className="timeline-stats">
          <Award size={20} className="timeline-stats-icon" />
          <div className="timeline-stats-content">
            <span className="timeline-stats-label">Unlocked</span>
            <span className="timeline-stats-value">{unlockProgress}%</span>
          </div>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="timeline-progress-section">
        <div className="timeline-progress-bar">
          <div 
            className="timeline-progress-fill" 
            style={{ width: `${unlockProgress}%` }}
          >
            <span className="timeline-progress-text">{unlockProgress}%</span>
          </div>
        </div>
      </div>
      
      {/* Timeline Controls */}
      <div className="timeline-controls">
        <div className="timeline-control-group">
          <button 
            className="timeline-control-btn" 
            onClick={resetView}
            title="Reset view"
          >
            <RotateCcw size={18} />
            <span>Reset</span>
          </button>
          <div className="timeline-zoom-controls">
            <button 
              className="timeline-control-btn timeline-zoom-btn" 
              onClick={handleZoomOut}
              title="Zoom out"
              disabled={zoomLevel <= 0.5}
            >
              <ZoomOut size={18} />
            </button>
            <span className="timeline-zoom-level">{Math.round(zoomLevel * 100)}%</span>
            <button 
              className="timeline-control-btn timeline-zoom-btn" 
              onClick={handleZoomIn}
              title="Zoom in"
              disabled={zoomLevel >= 3}
            >
              <ZoomIn size={18} />
            </button>
          </div>
        </div>
        <button
          className="timeline-play-btn"
          onClick={() => navigate('/play?category=timeline')}
        >
          <Play size={18} />
          <span>Play Timeline Trivia</span>
        </button>
      </div>
      
      {/* Era Navigation */}
      <div className="timeline-eras-nav">
        {eras.map(era => (
          <button
            key={era._id}
            className={`timeline-era-btn ${selectedEra === era.identifier ? 'active' : ''}`}
            style={{ backgroundColor: `${era.color}30`, borderColor: era.color }}
            onClick={() => handleEraClick(era.identifier)}
          >
            {era.name}
          </button>
        ))}
      </div>
      
      {/* Main Timeline Visualization */}
      <div 
        className="timeline-visualization"
        style={{ transform: `scale(${zoomLevel})` }}
      >
        <div className="timeline-line"></div>
        
        {eras.map(era => {
          // Only show selected era or all if none selected
          if (selectedEra && era.identifier !== selectedEra) return null;
          
          // Get events for this era
          const eraEvents = getEventsByEra(era.identifier);
          
          return (
            <div 
              key={era._id} 
              className="timeline-era"
              style={{ 
                borderColor: era.color,
                backgroundColor: `${era.color}10` 
              }}
            >
              <div className="timeline-era-header" style={{ backgroundColor: era.color }}>
                <h3>{era.name}</h3>
                <span className="timeline-era-years">
                  {Math.abs(era.startYear)} {era.startYear < 0 ? 'BCE' : 'CE'} - {Math.abs(era.endYear)} {era.endYear < 0 ? 'BCE' : 'CE'}
                </span>
              </div>
              
              <div className="timeline-events">
                {eraEvents.map(event => (
                  <div 
                    key={event._id}
                    className={`timeline-event ${event.isUnlocked ? 'unlocked' : 'locked'}`}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="timeline-event-marker" style={{ backgroundColor: event.isUnlocked ? era.color : '#64748b' }}>
                      {event.isUnlocked ? <Unlock size={14} /> : <Lock size={14} />}
                    </div>
                    <div className="timeline-event-card">
                      <div className="timeline-event-header">
                        <h4>{event.title}</h4>
                        {!event.isUnlocked && <Lock size={18} className="timeline-event-lock" />}
                      </div>
                      <div className="timeline-event-date">
                        <Clock size={14} />
                        <span>{event.approximateDate}</span>
                      </div>
                    </div>
                  </div>
                ))}
                
                {eraEvents.length === 0 && (
                  <div className="timeline-era-empty">
                    <p>No events unlocked in this era yet.</p>
                    <p>Keep playing to discover events!</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="timeline-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="timeline-event-detail" onClick={(e) => e.stopPropagation()}>
            <button 
              className="timeline-event-close-btn"
              onClick={() => setSelectedEvent(null)}
            >
              <X size={24} />
            </button>
            
            <div className="timeline-detail-header">
              <h3 className="timeline-event-title">{selectedEvent.title}</h3>
              <div className="timeline-event-meta">
                <span className="timeline-event-period">
                  <MapPin size={16} />
                  {selectedEvent.period}
                </span>
                <span className="timeline-event-date-detail">
                  <Clock size={16} />
                  {selectedEvent.approximateDate}
                </span>
              </div>
            </div>
          
          {selectedEvent.imageUrl && (
            <div className="timeline-event-image">
              <img src={selectedEvent.imageUrl} alt={selectedEvent.title} />
            </div>
          )}
          
          <div className="timeline-event-description">
            {selectedEvent.description}
          </div>
          
          <div className="timeline-event-references">
            <h4>Bible References:</h4>
            <ul>
              {selectedEvent.bibleReferences.map((ref, index) => (
                <li key={index}>{ref}</li>
              ))}
            </ul>
          </div>
          
          {selectedEvent.keyFigures.length > 0 && (
            <div className="timeline-event-figures">
              <h4>
                <Users size={18} />
                Key Figures
              </h4>
              <div className="timeline-event-figures-list">
                {selectedEvent.keyFigures.map((figure, index) => (
                  <span key={index} className="timeline-figure-tag">{figure}</span>
                ))}
              </div>
            </div>
          )}
          
          <div className="timeline-event-actions">
            <button 
              className="timeline-event-quiz-btn"
              onClick={() => navigate(`/play?category=timeline&event=${selectedEvent._id}`)}
            >
              <Play size={18} />
              Play Related Trivia
            </button>
          </div>
          </div>
        </div>
      )}
      
      {/* Help Section */}
      <div className="timeline-help">
        <div className="timeline-help-header">
          <BookOpen size={24} />
          <h3>Explore Biblical History</h3>
        </div>
        <p>
          Journey through biblical history from Creation to the Early Church. 
          Click on any event to learn more and test your knowledge with related trivia questions.
        </p>
        <div className="timeline-help-examples">
          <div className="timeline-help-example">
            <Unlock size={18} className="example-icon unlocked" />
            <span><strong>Available:</strong> All Events</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelinePage; 